import './App.css'
import './login.css'
import './features/asset-configuration/assetConfigStyle.css'
import { BrowserRouter as Router } from 'react-router-dom'
import Main from './features/dashboard/components/MainComponent'
import React, { useContext, createContext, useMemo } from 'react'
import { createTheme } from '@mui/material/styles'
import { ThemeProvider } from '@mui/material'
import { type IconKey } from './utils/AppUtil'

export interface SidebarItem {
  id: string
  label: string
  icon: IconKey
  to?: string
  onClick?: () => void
}

interface NavContextType {
  showSidebar: boolean
  setShowSidebar: (show: boolean) => void
  menuItems: SidebarItem[]
  setMenuItems: (items: SidebarItem[]) => void
  lastActiveItemId: string | null
  setLastActiveItemId: (id: string | null) => void
  activeSubAppId: string | null
  setActiveSubAppId: (id: string | null) => void
}

const NavContext = createContext<NavContextType | null>(null)

interface NavProviderProps {
  children: React.ReactNode
}

export function NavProvider ({ children }: NavProviderProps): React.ReactElement {
  const [showSidebar, setShowSidebarState] = React.useState(false)
  const [menuItems, setMenuItemsState] = React.useState<SidebarItem[]>([])
  const [lastActiveItemId, setLastActiveItemIdState] = React.useState<string | null>(null)
  const [activeSubAppId, setActiveSubAppIdState] = React.useState<string | null>(null)

  const setActiveSubAppId = (id: string | null): void => {
    setActiveSubAppIdState(id)
    sessionStorage.setItem('activeSubAppId', id ?? '')
  }

  const setShowSidebar = (show: boolean): void => {
    setShowSidebarState(show)
    sessionStorage.setItem('showSidebar', JSON.stringify(show))
  }
  const setMenuItems = (items: SidebarItem[]): void => {
    setMenuItemsState(items)
    sessionStorage.setItem('menuItems', JSON.stringify(items))
  }

  const setLastActiveItemId = (id: string | null): void => {
    setLastActiveItemIdState(id)
    sessionStorage.setItem('lastActiveItemId', id ?? '')
  }

  React.useEffect(() => {
    if (menuItems.length === 0) {
      const raw = sessionStorage.getItem('menuItems')
      if (raw !== null && raw !== undefined) {
        const parsed: SidebarItem[] = JSON.parse(raw)
        if (Array.isArray(parsed)) {
          setMenuItemsState(parsed)
        }
      }
    }
    if (lastActiveItemId === null || lastActiveItemId.trim() === '') {
      const rawId = sessionStorage.getItem('lastActiveItemId')
      if (rawId !== null && rawId.trim() !== '') setLastActiveItemIdState(rawId.trim())
    }

    if (!showSidebar) {
      const raw = sessionStorage.getItem('showSidebar')
      if (raw !== null) setShowSidebarState(JSON.parse(raw))
    }

    if (activeSubAppId === null || activeSubAppId.trim() === '') {
      const rawSubAppId = sessionStorage.getItem('activeSubAppId')
      if (rawSubAppId !== null && rawSubAppId.trim() !== '') setActiveSubAppIdState(rawSubAppId.trim())
    }
  }, [])

  const value = useMemo(
    () => ({
      showSidebar,
      setShowSidebar,
      menuItems,
      setMenuItems,
      lastActiveItemId,
      setLastActiveItemId,
      activeSubAppId,
      setActiveSubAppId
    }),
    [showSidebar, menuItems, lastActiveItemId, activeSubAppId]
  )

  return (
    <NavContext.Provider value={value}>
      {children}
    </NavContext.Provider>
  )
}

export function useNav (): NavContextType {
  const context = useContext(NavContext)
  if (context === null || context === undefined) throw new Error('useNav must be used within NavProvider')
  return context
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#64C3FF'
    },
    customGrey: {
      light: '#B0B0B0'
    }
  }
})

function App (): JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <div>
        <NavProvider>
          <Router>
            <Main />
          </Router>
        </NavProvider>
      </div>
    </ThemeProvider>
  )
}

export default App
